import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const CurrencyContext = createContext(null);

const KEY = "je_currency_v1";
const DEFAULT_RATE = 132;

export const CURRENCIES = [
  { code: "USD", label: "USD", symbol: "$" },
  { code: "HTG", label: "HTG", symbol: "G" },
];

export const CurrencyProvider = ({ children }) => {
  // Default to USD (diaspora-first) per user's choice
  const [currency, setCurrencyState] = useState(() => localStorage.getItem(KEY) || "USD");
  const [rate, setRate] = useState(DEFAULT_RATE); // HTG per 1 USD (admin-configured)

  useEffect(() => {
    localStorage.setItem(KEY, currency);
  }, [currency]);

  // Fetch admin-configured Zelle rate at mount and every 5 minutes
  useEffect(() => {
    let cancelled = false;
    const fetchRate = () => {
      api
        .get("/exchange-rate")
        .then(({ data }) => {
          if (!cancelled && data?.htg_per_usd) setRate(Number(data.htg_per_usd));
        })
        .catch(() => {});
    };
    fetchRate();
    const id = setInterval(fetchRate, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const setCurrency = (c) => setCurrencyState(c);
  const toggleCurrency = () => setCurrencyState((c) => (c === "USD" ? "HTG" : "USD"));

  // Convert an amount stored in HTG to the currently selected currency
  const convert = useCallback(
    (htgAmount) => {
      if (htgAmount === null || htgAmount === undefined) return 0;
      const n = Number(htgAmount);
      if (currency === "HTG") return n;
      return n / (rate || DEFAULT_RATE);
    },
    [currency, rate]
  );

  // Format an HTG amount into the selected currency string
  const format = useCallback(
    (htgAmount) => {
      if (htgAmount === null || htgAmount === undefined || isNaN(Number(htgAmount))) return "";
      const converted = convert(htgAmount);
      if (currency === "USD") {
        return (
          "$" +
          new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(converted)
        );
      }
      return (
        new Intl.NumberFormat("fr-HT", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(converted) + " HTG"
      );
    },
    [currency, convert]
  );

  return (
    <CurrencyContext.Provider
      value={{ currency, setCurrency, toggleCurrency, rate, convert, format }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
